import {
    ICS2Notification,
    ICS2NotificationBody,
    ICS2NotificationError,
} from 'store/declarations/eu/ics2/ics2-notification';
import {
    ICS2ArrivalNotificationMessage,
    ICS2ArrivalView,
} from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';

export const ArrivalMessageTypes = {
    REGISTRATION: 'IE3R04',
    REJECTION: 'IE3N99',
    HOUSE_STATE: 'IE3N07',
    CONTROL: 'IE3N08',
} as const;

export type ArrivalMessageType = typeof ArrivalMessageTypes[keyof typeof ArrivalMessageTypes];

type ProcessingMarker = {
    outcome?: string;
    reason?: string;
};

export const toIcs2Notification = (message: ICS2ArrivalNotificationMessage): ICS2Notification => ({
    notificationId: message.messageUuid,
    messageType: message.messageType,
    createdAt: message.receivedAt,
    notification: message.details as ICS2NotificationBody | undefined,
    rawMessage: message.rawMessage,
});

export const errorsOf = (message: ICS2ArrivalNotificationMessage | undefined): ICS2NotificationError[] =>
    ((message?.details as ICS2NotificationBody | undefined)?.error as ICS2NotificationError[] | undefined) ?? [];

/**
 * Why the service recorded the message without acting on it, if it did.
 *
 * Only an IE3N07 or IE3N08 is ever marked this way. A superseded rejection carries no marker at
 * all, which is what `belongsToCurrentAttempt` is for.
 */
export const ignoredReason = (message: ICS2ArrivalNotificationMessage): string | undefined => {
    const marker = (message.details as Record<string, unknown> | undefined)?._processing as
        | ProcessingMarker
        | undefined;

    return marker?.outcome === 'IGNORED' ? marker.reason ?? 'IGNORED' : undefined;
};

/**
 * Whether the message answers the submission the arrival is currently on.
 *
 * A rejection of an earlier attempt stays in the history and looks no different from a current one,
 * so anything that presents a message as the state of the arrival has to ask this first. When the
 * arrival has no submission recorded, staleness cannot be established and the message is taken at
 * face value rather than hidden.
 */
export const belongsToCurrentAttempt = (
    message: ICS2ArrivalNotificationMessage,
    arrival: ICS2ArrivalView | undefined
): boolean => {
    const currentRequestId = arrival?.submission?.requestId;
    if (!currentRequestId || !message.requestId) return true;

    return message.requestId === currentRequestId;
};

/**
 * The message of a type that currently speaks for the arrival: the most recent one that the service
 * acted on and that belongs to the attempt in flight. The list arrives newest first.
 */
export const latestActionable = (
    messages: ICS2ArrivalNotificationMessage[],
    type: ArrivalMessageType,
    arrival: ICS2ArrivalView | undefined
): ICS2ArrivalNotificationMessage | undefined =>
    messages.find(
        (message) =>
            message.messageType === type && !ignoredReason(message) && belongsToCurrentAttempt(message, arrival)
    );

/**
 * `details` is the parsed message as the service's JSON mapper rendered it, so the keys are the
 * ICS2 XML element names rather than the camelCase the rest of the app uses - `MRN`, `LRN`, and
 * dates wrapped in a `DateTime` member. The readers below take the value as it comes.
 */
export const readField = (message: ICS2ArrivalNotificationMessage | undefined, path: string[]): unknown =>
    path.reduce<unknown>(
        (value, key) => (value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined),
        message?.details
    );

export const readText = (message: ICS2ArrivalNotificationMessage | undefined, path: string[]): string | undefined => {
    const value = readField(message, path);
    return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;
};

export const readDateTime = (
    message: ICS2ArrivalNotificationMessage | undefined,
    path: string[]
): string | undefined => {
    const value = readField(message, path);
    if (typeof value === 'string') return value;

    const wrapped = (value as { DateTime?: unknown } | undefined)?.DateTime;
    return typeof wrapped === 'string' ? wrapped : undefined;
};

export const asList = (value: unknown): Record<string, unknown>[] => {
    if (Array.isArray(value)) return value.filter((entry): entry is Record<string, unknown> => !!entry);
    return value && typeof value === 'object' ? [value as Record<string, unknown>] : [];
};
